/**
 * PATH: packages/ui/src/kanban/components/settings-home/useSettingsUndo.ts
 * FILE_ID: UI-KANBAN-SETTINGS-UNDO-001
 * PURPOSE: One-step undo plus scoped reset confirmation for preference drafts.
 * ROLE IN ARCHITECTURE: Session-local chrome helper for SettingsHome.
 * MAIN EXPORTS: useSettingsUndo, countChangedControls, confirmScopedReset.
 * NON-RESPONSIBILITIES: Does not persist or talk to the preference store.
 * NOTES FOR FUTURE AI: One step only. Confirm only when more than one control differs.
 */

import { useCallback, useState } from 'react'

export function countChangedControls(current: unknown, baseline: unknown): number {
  const flatten = (value: unknown, prefix = ''): Record<string, string> => {
    if (value === null || value === undefined) return { [prefix || '$']: String(value) }
    if (typeof value !== 'object') return { [prefix || '$']: JSON.stringify(value) }
    if (Array.isArray(value)) return { [prefix || '$']: JSON.stringify(value) }
    const next: Record<string, string> = {}
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      Object.assign(next, flatten(child, prefix ? `${prefix}.${key}` : key))
    }
    return next
  }
  const left = flatten(current)
  const right = flatten(baseline)
  const keys = new Set([...Object.keys(left), ...Object.keys(right)])
  let count = 0
  for (const key of keys) {
    if (left[key] !== right[key]) count += 1
  }
  return count
}

/** Confirm when more than one scoped control would reset. */
export function confirmScopedReset(changedCount: number, message: string): boolean {
  if (changedCount <= 1) return true
  if (typeof window === 'undefined' || typeof window.confirm !== 'function') return true
  return window.confirm(message)
}

export function useSettingsUndo<T>(_initial?: T) {
  const [snapshot, setSnapshot] = useState<T | null>(null)

  const remember = useCallback((previous: T) => {
    setSnapshot(previous)
  }, [])

  const undo = useCallback((): T | null => {
    const previous = snapshot
    setSnapshot(null)
    return previous
  }, [snapshot])

  const clear = useCallback(() => setSnapshot(null), [])

  return { canUndo: snapshot !== null, remember, undo, clear }
}
